import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Modal, ModalDialog, ModalClose, Input, Textarea, Stack,
  Grid, Card, Avatar, Chip, Divider, IconButton
} from '@mui/joy';
import { supabase } from '../../supabaseClient';
import EnhancedButton from '../../components/EnhancedButton';
import EnhancedInput from '../../components/EnhancedInput';
import MetricCard from '../../components/MetricCard';
import LoadingSkeleton, { CardSkeleton } from '../../components/LoadingSkeleton';
import dayjs from 'dayjs';

const AnnouncementAdmin = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [form, setForm] = useState({
    title: '',
    body: '',
    expires_at: '',
  });

  const loadAnnouncements = async () => {
    setLoading(true);
    const { data, error } = await supabase.from('announcements').select('*').order('posted_at', { ascending: false });
    if (!error) setAnnouncements(data);
    setLoading(false);
  };
  useEffect(() => { loadAnnouncements(); }, []);

  const handleSubmit = async () => {
    const payload = {
      ...form,
      expires_at: form.expires_at || null
    };
    if (editingAnnouncement) {
      await supabase.from('announcements').update(payload).eq('id', editingAnnouncement.id);
    } else {
      await supabase.from('announcements').insert([payload]);
    }
    setOpenModal(false);
    setEditingAnnouncement(null);
    await loadAnnouncements();
  };

  const handleDelete = async (id) => {
    const confirmDelete = window.confirm('Are you sure you want to delete this announcement?');
    if (!confirmDelete) return;

    console.log('Attempting to delete ID:', id);

    // The following matchTest block is no longer needed and has been removed.
    // const { data: matchTest, error: matchError } = await supabase
    //   .from('announcements')
    //   .select()
    //   .match({ id });
    // console.log('Delete match test:', matchTest, matchError);

    const { error } = await supabase
      .from('announcements')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Delete error:', error.message);
    } else {
      console.log('Successfully deleted announcement with ID:', id);
      await loadAnnouncements();
    }
  };

  // Helper functions
  const getAnnouncementInitials = (title) => {
    return title?.split(' ').map(word => word[0]).join('').toUpperCase().substring(0, 2) || 'AN';
  };

  const getAnnouncementColor = (index) => {
    const colors = ['#00A884', '#17a2b8', '#28a745', '#6c757d', '#ffc107', '#dc3545'];
    return colors[index % colors.length];
  };

  const isExpired = (expiresAt) => {
    return expiresAt && dayjs(expiresAt).isBefore(dayjs());
  };

  const isExpiringSoon = (expiresAt) => {
    return expiresAt && dayjs(expiresAt).isBefore(dayjs().add(7, 'days')) && !isExpired(expiresAt);
  };

  // Statistics
  const totalAnnouncements = announcements.length;
  const activeAnnouncements = announcements.filter(a => !isExpired(a.expires_at)).length;
  const expiredAnnouncements = announcements.filter(a => isExpired(a.expires_at)).length;
  const expiringSoon = announcements.filter(a => isExpiringSoon(a.expires_at)).length;

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        {/* Analytics Loading */}
        <Box sx={{ mb: 4 }}>
          <LoadingSkeleton height="32px" width="200px" sx={{ mb: 3 }} />
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid xs={12} sm={6} md={3}><CardSkeleton /></Grid>
            <Grid xs={12} sm={6} md={3}><CardSkeleton /></Grid>
            <Grid xs={12} sm={6} md={3}><CardSkeleton /></Grid>
            <Grid xs={12} sm={6} md={3}><CardSkeleton /></Grid>
          </Grid>
        </Box>
        
        {/* Announcements Loading */}
        <Box sx={{ mt: 4 }}>
          <Grid container spacing={3}>
            {[...Array(4)].map((_, i) => (
              <Grid key={i} xs={12} sm={6} md={6}><CardSkeleton /></Grid>
            ))}
          </Grid>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Announcement Analytics */}
      <Box sx={{ mb: 4 }}>
        <Typography 
          level="h4" 
          sx={{ 
            mb: 3, 
            color: 'success.700',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5
          }}
          className="fade-in"
        >
          <span className="material-symbols-outlined" style={{ fontSize: '24px', fontFamily: 'Material Symbols Outlined' }}>
            campaign
          </span>
          Announcement Analytics
        </Typography>
        
        <Grid container spacing={3} sx={{ mb: 4 }} className="fade-in-up">
          <Grid xs={12} sm={6} md={3}>
            <MetricCard
              title="Total Announcements"
              value={totalAnnouncements}
              icon="campaign"
              gradientColors={['#00A884', '#007E33']}
            />
          </Grid>
          <Grid xs={12} sm={6} md={3}>
            <MetricCard
              title="Active Announcements"
              value={activeAnnouncements}
              icon="notifications_active"
              gradientColors={['#28a745', '#218838']}
            />
          </Grid>
          <Grid xs={12} sm={6} md={3}>
            <MetricCard
              title="Expiring Soon"
              value={expiringSoon}
              icon="schedule"
              gradientColors={['#ffc107', '#e0a800']}
            />
          </Grid>
          <Grid xs={12} sm={6} md={3}>
            <MetricCard
              title="Expired"
              value={expiredAnnouncements}
              icon="notifications_off"
              gradientColors={['#6c757d', '#5a6268']}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Add Announcement Section */}
      <Card 
        sx={{ 
          p: 3, 
          mb: 4,
          background: 'linear-gradient(135deg, rgba(0, 168, 132, 0.02) 0%, rgba(0, 126, 51, 0.02) 100%)',
          border: '1px solid',
          borderColor: 'success.200',
        }}
        className="fade-in"
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography 
              level="h4" 
              sx={{ 
                color: 'success.700',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                mb: 1
              }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: '24px', fontFamily: 'Material Symbols Outlined' }}>
                speaker_notes
              </span>
              Announcement Management
            </Typography>
            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
              Create, edit, and manage system announcements and notifications
            </Typography>
          </Box>
          
          <EnhancedButton
            gradient
            onClick={() => { setForm({ title: '', body: '', expires_at: '' }); setEditingAnnouncement(null); setOpenModal(true); }}
            startDecorator={
              <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
                add
              </span>
            }
          >
            Add Announcement
          </EnhancedButton>
        </Box>
      </Card>

      {/* Announcements Grid */}
      <Box className="fade-in-up">
        <Typography 
          level="h4" 
          sx={{ 
            mb: 3, 
            color: 'success.700',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5
          }}
        >
          <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
            view_list
          </span>
          Announcement Board ({totalAnnouncements})
        </Typography>
        
        <Grid container spacing={3}>
          {announcements.map((announcement, index) => (
            <Grid key={announcement.id} xs={12} md={6}>
              <Card 
                sx={{ 
                  height: '100%',
                  cursor: 'pointer',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 25px rgba(0, 168, 132, 0.15)',
                  },
                  ...(isExpired(announcement.expires_at) && {
                    opacity: 0.6,
                    background: 'linear-gradient(135deg, rgba(108, 117, 125, 0.1) 0%, rgba(90, 98, 104, 0.05) 100%)',
                  })
                }}
                className="hover-lift"
              >
                {/* Announcement Header */}
                <Box 
                  sx={{ 
                    p: 3, 
                    background: `linear-gradient(135deg, ${getAnnouncementColor(index)}15 0%, ${getAnnouncementColor(index)}05 100%)`,
                    borderBottom: '1px solid',
                    borderColor: 'neutral.200'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Avatar 
                      sx={{ 
                        bgcolor: getAnnouncementColor(index),
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '0.875rem'
                      }}
                    >
                      {getAnnouncementInitials(announcement.title)}
                    </Avatar>
                    
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography 
                        level="title-sm" 
                        sx={{ 
                          color: 'success.700',
                          fontWeight: 600,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {announcement.title || 'Untitled Announcement'}
                      </Typography>
                      <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                        📅 Posted: {dayjs(announcement.posted_at).format('MMM D, YYYY')}
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {isExpired(announcement.expires_at) ? (
                      <Chip size="sm" variant="soft" color="neutral">
                        <span className="material-symbols-outlined" style={{ fontSize: '14px', fontFamily: 'Material Symbols Outlined' }}>notifications_off</span>
                        Expired
                      </Chip>
                    ) : isExpiringSoon(announcement.expires_at) ? (
                      <Chip size="sm" variant="soft" color="warning">
                        <span className="material-symbols-outlined" style={{ fontSize: '14px', fontFamily: 'Material Symbols Outlined' }}>schedule</span>
                        Expiring Soon
                      </Chip>
                    ) : (
                      <Chip size="sm" variant="soft" color="success">
                        <span className="material-symbols-outlined" style={{ fontSize: '14px', fontFamily: 'Material Symbols Outlined' }}>notifications_active</span>
                        Active
                      </Chip>
                    )}
                    
                    {announcement.expires_at && (
                      <Chip 
                        size="sm" 
                        variant="soft" 
                        sx={{
                          bgcolor: `${getAnnouncementColor(index)}20`,
                          color: getAnnouncementColor(index)
                        }}
                        startDecorator={
                          <span className="material-symbols-outlined" style={{ fontSize: '14px', fontFamily: 'Material Symbols Outlined' }}>
                            event
                          </span>
                        }
                      >
                        Expires: {dayjs(announcement.expires_at).format('MMM D')}
                      </Chip>
                    )}
                  </Box>
                </Box>

                {/* Announcement Details */}
                <Box sx={{ p: 3 }}>
                  <Typography 
                    level="body-sm" 
                    sx={{ 
                      mb: 3,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical'
                    }}
                  >
                    {announcement.body || 'No content provided'}
                  </Typography>

                  <Divider sx={{ my: 2 }} />

                  {/* Action Buttons */}
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <EnhancedButton
                      size="sm"
                      variant="outlined"
                      onClick={() => { setEditingAnnouncement(announcement); setForm(announcement); setOpenModal(true); }}
                      startDecorator={
                        <span className="material-symbols-outlined" style={{ fontSize: '16px', fontFamily: 'Material Symbols Outlined' }}>
                          edit
                        </span>
                      }
                      sx={{ flex: 1 }}
                    >
                      Edit
                    </EnhancedButton>
                    
                    <EnhancedButton 
                      size="sm" 
                      color="danger" 
                      variant="outlined" 
                      onClick={() => handleDelete(announcement.id)}
                      startDecorator={
                        <span className="material-symbols-outlined" style={{ fontSize: '16px', fontFamily: 'Material Symbols Outlined' }}>
                          delete
                        </span>
                      }
                      sx={{ flex: 1 }}
                    >
                      Delete
                    </EnhancedButton>
                  </Box>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Enhanced Announcement Modal */}
      <Modal open={openModal} onClose={() => { setOpenModal(false); setEditingAnnouncement(null); }}>
        <ModalDialog 
          sx={{ 
            maxWidth: '600px',
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
            backdropFilter: 'blur(15px)',
            border: '1px solid',
            borderColor: 'success.200',
          }}
        >
          <ModalClose />
          <Typography 
            level="h4" 
            sx={{ 
              mb: 3,
              color: 'success.700',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5
            }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: '24px', fontFamily: 'Material Symbols Outlined' }}>
              {editingAnnouncement ? 'edit' : 'speaker_notes'}
            </span>
            {editingAnnouncement ? 'Edit Announcement' : 'Add New Announcement'}
          </Typography>
          
          <Grid container spacing={2}>
            <Grid xs={12}>
              <EnhancedInput
                label="Announcement Title"
                value={form.title}
                onChange={e => setForm({ ...form, title: e.target.value })}
                placeholder="Important System Update"
                required
                startDecorator={
                  <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
                    campaign
                  </span>
                }
              />
            </Grid>
            
            <Grid xs={12}>
              <Box sx={{ mb: 2 }}>
                <Typography level="body-sm" sx={{ mb: 1, fontWeight: 500 }}>Announcement Content</Typography>
                <Textarea
                  value={form.body}
                  onChange={e => setForm({ ...form, body: e.target.value })}
                  placeholder="Announcement details and information..."
                  minRows={4}
                  sx={{
                    '&:focus-within': {
                      borderColor: 'success.500',
                      boxShadow: '0 0 0 2px rgba(0, 168, 132, 0.25)',
                    },
                  }}
                />
              </Box>
            </Grid>
            
            <Grid xs={12}>
              <EnhancedInput
                label="Expiration Date (Optional)"
                type="datetime-local"
                value={form.expires_at}
                onChange={e => setForm({ ...form, expires_at: e.target.value })}
                startDecorator={
                  <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
                    schedule
                  </span>
                }
              />
            </Grid>
            
            <Grid xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                <EnhancedButton
                  variant="outlined"
                  onClick={() => { setOpenModal(false); setEditingAnnouncement(null); }}
                >
                  Cancel
                </EnhancedButton>
                <EnhancedButton
                  gradient
                  onClick={handleSubmit}
                  startDecorator={
                    <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
                      {editingAnnouncement ? 'save' : 'add'}
                    </span>
                  }
                >
                  {editingAnnouncement ? 'Update Announcement' : 'Create Announcement'}
                </EnhancedButton>
              </Box>
            </Grid>
          </Grid>
        </ModalDialog>
      </Modal>
    </Box>
  );
};

export default AnnouncementAdmin;
